import type { AntTreeNodeProps, DataNode } from "antd/es/tree";
import {
  ConsecutiveBreaker,
  circuitBreaker,
  handleAll,
  retry,
  wrap,
} from "cockatiel";
import type React from "react";
import { Spinner } from "react-bootstrap";
import { FileEarmark } from "react-bootstrap-icons";
import {
  type ExternalDocument,
  type ExternalFolder,
  ExternalPartners,
} from "@/briefpoint-client";
import { PopupNavigator } from "@/components/ExternalPartner/AuthPopup/PopupNavigator";
import { GetExternalPartnerAuthUrl } from "@/components/ExternalPartner/ExternalPartnerConnection";
import { useUserApi as UseUserApi } from "@/hooks/useApi";
import { useAuth as UseAuth } from "@/hooks/useAuth";
import UseConfig from "@/hooks/useConfig";
import FolderIcon from "@/images/folder-icon.svg?react";
import ClioLogo from "@/images/Glyph_Clio Glyph Blue.svg?react";
// IMAGES
import BriefpointLogo from "@/images/logo.svg?react";
import MyCaseLogo from "@/images/MyCase.svg?react";
import SmokeballLogo from "@/images/Smokeball_logo_mark.svg?react";
import SwitcherClosed from "@/images/tree-switcher-closed.svg?react";
import SwitchedOpened from "@/images/tree-switcher-opened.svg?react";
import type {
  ExtendedExternalFolder,
  ExternalClientIEnumerableExternalPartnerResponseResponse,
  ExternalDocumentResponse,
  UserResponse,
} from "@/lib/types";
import { readParams } from "@/lib/utils";

interface TreeNode extends DataNode {
  expanded?: boolean;
}

interface TreeNodeWithPath extends DataNode {
  path?: string;
}

export function delayDocumentRefresh(
  delay: number,
  loadDocuments: () => Promise<void>,
  setState: React.Dispatch<React.SetStateAction<boolean>>,
  finishAction: () => void,
): void {
  setTimeout(() => {
    loadDocuments();
    setState(false);
    finishAction();
  }, delay);
}

export function getCaseSourceLogo(partner?: ExternalPartners) {
  if (!partner || partner === ExternalPartners.None) {
    return <BriefpointLogo title="Briefpoint Case" />;
  }

  switch (partner) {
    case ExternalPartners.Clio:
      return <ClioLogo title="Clio Case" />;
    case ExternalPartners.Smokeball:
      return <SmokeballLogo title="Smokeball Case" />;
    case ExternalPartners.MyCase:
      return <MyCaseLogo title="MyCase Case" />;
    default:
      return <BriefpointLogo title="Briefpoint Case" />;
  }
}

export const initiateAuth = async () => {
  const { user, setUser, firm } = UseAuth();
  const userApi = UseUserApi();
  const [config] = UseConfig();

  if (!user || !firm) {
    return null;
  }
  const userId = user.id;
  const firmProviders = firm.preferences?.enabledExternalProviders?.values();
  const enabledPartners = firmProviders ? Array.from(firmProviders) : null;

  if (!enabledPartners?.length) {
    return null;
  }

  const partner = enabledPartners[0];
  const authUrl = GetExternalPartnerAuthUrl(partner, userId, config);
  const popupNavigator = new PopupNavigator();
  const handle = await popupNavigator.prepare({});

  if (!authUrl) {
    // TODO: probably should throw an error here
    return null;
  }

  try {
    const result = await handle.navigate({
      url: authUrl,
    });

    const params = readParams(result.url);
    const code = params.get("code");
    if (code) {
      const tokenResult = await userApi.userExternalPartnerAuth({
        userId,
        code,
        partner: partner,
      });
      if (tokenResult.responseCode !== 200) {
        throw new Error(
          `Unable to authenticate to ${partner}. Status Code: ${tokenResult.responseCode}`,
        );
      }

      setUser((u) => {
        const newUser = { ...u } as UserResponse;
        newUser.externalConnection = { partner: partner, isActive: true };
        return newUser;
      });
    } else {
      // setSaving(false);
      throw new Error(`Unexpected response from auth: ${params}`);
    }
    // setSaving(false);
  } catch (_e) {
    let message = "";
    if (typeof _e === "string") {
      message = _e;
    } else if (_e instanceof Error) {
      message = _e.message;
    }

    if (message.includes("closed by user")) {
      // setFormError("Authentication cancelled");
      throw new Error("Authentication cancelled");
    }
    // setSaving(false);
  }
};

/**
 * Builds a map of folder IDs to their hasPreviouslySelected status
 * @param folders - The folder structure to process
 * @returns A Map where keys are folder IDs and values indicate if the folder has been previously viewed
 */
function buildFolderViewedMap(folders: ExternalFolder[]): Map<string, boolean> {
  const folderViewedMap = new Map<string, boolean>();
  const buildRecursive = (folderList: ExternalFolder[]): void => {
    for (const folder of folderList) {
      if (folder.id) {
        const extendedFolder = folder as ExtendedExternalFolder;
        folderViewedMap.set(
          folder.id,
          extendedFolder.hasPreviouslySelected === true,
        );
      }
      if (folder.folders && folder.folders.length > 0) {
        buildRecursive(folder.folders);
      }
    }
  };
  buildRecursive(folders);
  return folderViewedMap;
}

/**
 * Determines if a folder should show a loading spinner based on its state
 * @param isLoading - Whether data is currently loading
 * @param folderId - The folder ID to check
 * @param folderViewedMap - Map of folder IDs to their viewed status
 * @returns True if the folder should show loading spinner, false otherwise
 */
function shouldShowLoading(
  isLoading: boolean,
  folderId: string | undefined,
  folderViewedMap: Map<string, boolean>,
): boolean {
  if (!isLoading || !folderId) return false;
  const hasBeenViewed = folderViewedMap.get(folderId) === true;
  return !hasBeenViewed;
}

/**
 * Creates a placeholder node for empty folders (either "No items" or loading spinner)
 * @param folderKey - The key of the folder
 * @param shouldShowLoading - Whether to show loading spinner
 * @param path - Optional path for the placeholder node
 * @returns A tree node for the placeholder
 */
function createEmptyFolderPlaceholder(
  folderKey: React.Key,
  shouldShowLoading: boolean,
  path?: string,
): TreeNode {
  return {
    title: shouldShowLoading ? (
      <>
        <Spinner size="sm" className="me-2" />
        Loading...
      </>
    ) : (
      "No items"
    ),
    key: `${folderKey}-no-items`,
    disabled: true,
    selectable: false,
    icon: " ", // Leave it blank to prevent any icon from showing
    ...(path && { path }),
  } as TreeNode;
}

/**
 * Formats a document name, ensuring the type extension is included
 * @param name - The document name (can be null, undefined, or string)
 * @param type - The document type/extension (can be null, undefined, or string)
 * @returns The formatted document name with extension
 */
function formatDocumentName(
  name: string | null | undefined,
  type: string | null | undefined,
): string {
  const docName = name || "Unnamed Document";
  const docType = type || "";
  return docName.endsWith(docType) ? docName : `${docName}${docType}`;
}

/**
 * Marks the first folder in a tree structure with isFirstFolder flag
 * This allows the switcher icon to be hidden for the first folder
 * @param treeData - The tree data to process (can be TreeNode[] or TreeNodeWithPath[])
 * @returns True if a first folder was found and marked, false otherwise
 */
function markFirstFolderInTree(
  treeData: Array<TreeNode | TreeNodeWithPath>,
): boolean {
  const firstFolderKey = findFirstFolderKey(treeData);
  if (!firstFolderKey) return false;

  const markRecursive = (
    nodes: Array<TreeNode | TreeNodeWithPath>,
  ): boolean => {
    for (const node of nodes) {
      // Check if this is the first folder (not a leaf node)
      if (node.key === firstFolderKey && !node.isLeaf) {
        (
          node as (TreeNode | TreeNodeWithPath) & {
            isFirstFolder?: boolean;
          }
        ).isFirstFolder = true;
        return true;
      }
      if (node.children && node.children.length > 0) {
        if (
          markRecursive(node.children as Array<TreeNode | TreeNodeWithPath>)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  return markRecursive(treeData);
}

// Function to transform data into a tree structure (for importing from External Partner to BP)
export const transformDataExternalToBp = (
  folders: ExternalFolder[],
  isLoading = false,
) => {
  const tree: TreeNode[] = [];
  const dataMap: Map<string, ExternalDocumentResponse> = new Map();
  const folderMap = new Map<string, { node: TreeNode; path: string }>();
  const folderKeys = new Set<string>();

  // Build a map of folder IDs to their hasPreviouslySelected status
  const folderViewedMap = buildFolderViewedMap(folders);

  // Helper function to recursively process folders
  const processFolder = (
    folder: ExternalFolder,
    parentPath = "",
    parentChildren: DataNode[] = tree,
  ): void => {
    if (!folder) return;

    // Skip root folder if it has no name (typically root folder has id: null, name: null)
    const isRootFolder = !folder.name && !folder.id;

    if (isRootFolder) {
      // For root folder without name, process its children directly into parentChildren
      if (folder.folders && folder.folders.length > 0) {
        for (const subFolder of folder.folders) {
          processFolder(subFolder, parentPath, parentChildren);
        }
      }
      return;
    }

    // Create folder node for actual folders
    const folderName = folder.name || "Unnamed Folder";
    const folderPath = parentPath ? `${parentPath}/${folderName}` : folderName;

    const folderNode: TreeNode = {
      title: folderName,
      key: folder.id || folderPath,
      children: [], // Always initialize with empty array to ensure switcher icon shows
      icon: <FolderIcon />,
      isLeaf: false, // Explicitly mark as not a leaf to ensure switcher icon displays
    };

    // Track this as a folder key
    folderKeys.add(folder.id || folderPath);

    // Add to parent children
    parentChildren.push(folderNode);

    // Store in folder map for document matching
    if (folder.id) {
      folderMap.set(folder.id, { node: folderNode, path: folderPath });
    }

    // Process nested folders recursively
    if (folder.folders && folder.folders.length > 0) {
      for (const subFolder of folder.folders) {
        processFolder(subFolder, folderPath, folderNode.children);
      }
    }
  };

  // Build folder structure first
  for (const folder of folders) {
    processFolder(folder);
  }

  // Helper function to collect all documents
  const collectDocuments = (
    folderList: ExternalFolder[],
  ): ExternalDocument[] => {
    const docs: ExternalDocument[] = [];
    for (const folder of folderList) {
      if (folder.documents && folder.documents.length > 0) {
        docs.push(...folder.documents);
      }
      if (folder.folders && folder.folders.length > 0) {
        docs.push(...collectDocuments(folder.folders));
      }
    }
    return docs;
  };

  // Get all documents and add them to appropriate folders or root
  const allDocuments = collectDocuments(folders);

  for (const doc of allDocuments) {
    if (!doc.id) continue;

    // Store document in dataMap (cast to ExternalDocumentResponse for compatibility)
    dataMap.set(doc.id, doc as ExternalDocumentResponse);

    const docNode: TreeNode = {
      title: formatDocumentName(doc.name, doc.type),
      key: doc.id,
      isLeaf: true,
      icon: <FileEarmark />,
    };

    // Match document to folder based on folder field
    if (doc.folder && folderMap.has(doc.folder)) {
      const folderInfo = folderMap.get(doc.folder);
      if (folderInfo?.node.children) {
        folderInfo.node.children.push(docNode);
      }
    } else {
      // Document belongs to root
      tree.push(docNode);
    }
  }

  // Helper function to recursively add "No items" or loading spinner to empty folders
  const addNoItemsToEmptyFolders = (nodes: TreeNode[]): void => {
    for (const node of nodes) {
      // Check if this is a folder (has children property and is not a leaf)
      if (node.children && !node.isLeaf) {
        if (node.children.length === 0) {
          const folderId = node.key?.toString();
          const showLoading = shouldShowLoading(
            isLoading,
            folderId,
            folderViewedMap,
          );
          node.children.push(
            createEmptyFolderPlaceholder(node.key, showLoading),
          );
        } else {
          // Recursively check nested folders
          addNoItemsToEmptyFolders(node.children);
        }
      }
    }
  };

  // Add "No items" or loading spinner to any empty folders
  addNoItemsToEmptyFolders(tree);

  // Mark the first folder so we can hide its switcher icon
  markFirstFolderInTree(tree);

  return [tree, dataMap, folderKeys];
};

// Function to transform data into a tree structure (for exporting from BP Partner to External Partner)
export const transformDataBpToExternal = (
  data: ExternalFolder[],
  parentPath = "",
  isLoading = false,
): TreeNodeWithPath[] => {
  const result: TreeNodeWithPath[] = [];
  const processedKeys = new Set<string>();

  // Build a map of folder IDs to their hasPreviouslySelected status
  const folderViewedMap = buildFolderViewedMap(data);

  for (const item of data) {
    if (!item) continue;

    // Skip root folder if it has no name (typically root folder has id: null, name: null)
    const isRootFolder = !item.name && !item.id;

    if (isRootFolder) {
      // For root folder without name, process its children directly into result
      if (item.folders && item.folders.length > 0) {
        const rootChildren = transformDataBpToExternal(
          item.folders,
          parentPath,
          isLoading,
        );
        result.push(...rootChildren);
      }
      // Also add root-level documents if any
      if (item.documents && item.documents.length > 0) {
        const docChildren = item.documents.map((doc) => {
          const docKey =
            doc.id ?? `${parentPath}/${formatDocumentName(doc.name, doc.type)}`;
          return {
            title: formatDocumentName(doc.name, doc.type),
            key: docKey,
            icon: <FileEarmark />,
            selectable: false,
            isLeaf: true,
            path: `${parentPath}/${formatDocumentName(doc.name, doc.type)}`,
          };
        });
        result.push(...docChildren);
      }
      continue;
    }

    const currentPath = parentPath ? `${parentPath}/${item.name}` : item.name;
    const itemKey = item.id ?? `${currentPath}_${item.name}`;

    // Prevent duplicate processing of the same item
    if (processedKeys.has(itemKey)) {
      continue;
    }
    processedKeys.add(itemKey);

    // Process nested folders recursively first
    const folderChildren = item.folders
      ? transformDataBpToExternal(item.folders, currentPath || "", isLoading)
      : [];

    // Filter out any null entries and ensure uniqueness for folders
    const uniqueFolderChildren = folderChildren.filter(
      (child, index, self) =>
        self.findIndex((c) => c?.key === child.key) === index,
    );

    const children: TreeNodeWithPath[] = [...uniqueFolderChildren];

    // Add documents as leaf nodes to the children array
    if (item.documents && item.documents.length > 0) {
      const docChildren = item.documents.map((doc) => {
        const formattedName = formatDocumentName(doc.name, doc.type);
        const docKey = doc.id ?? `${currentPath}/${formattedName}`;
        return {
          title: formattedName,
          key: docKey,
          icon: <FileEarmark />,
          selectable: false,
          isLeaf: true,
          path: `${currentPath}/${formattedName}`,
        };
      });

      // Ensure document uniqueness
      const uniqueDocChildren = docChildren.filter(
        (child, index, self) =>
          self.findIndex((c) => c.key === child.key) === index,
      );
      children.push(...uniqueDocChildren);
    }

    // If folder has no children, add "No items" placeholder or loading spinner
    if (children.length === 0) {
      const showLoading = shouldShowLoading(
        isLoading,
        itemKey,
        folderViewedMap,
      );
      children.push(
        createEmptyFolderPlaceholder(
          itemKey,
          showLoading,
          `${currentPath ?? ""}/no-items`,
        ) as TreeNodeWithPath,
      );
    }

    result.push({
      title: item.name ?? "",
      key: itemKey,
      selectable: true,
      icon: <FolderIcon />,
      children,
      path: currentPath ?? undefined,
    });
  }

  // Mark the first folder so we can hide its switcher icon
  markFirstFolderInTree(result);

  return result;
};

export function TreeSwitcherIcon(props: AntTreeNodeProps): React.ReactNode {
  // Hide switcher icon for the first folder
  const nodeData = props.data as (TreeNode | TreeNodeWithPath) & {
    isFirstFolder?: boolean;
  };
  if (nodeData?.isFirstFolder) {
    return null;
  }
  return props.expanded ? <SwitchedOpened /> : <SwitcherClosed />;
}

export async function externalRequestWrapper(fetchData: unknown, attempts = 1) {
  // Attempt to retry if no response/timeout/network error occurs
  const retryPolicy = retry(handleAll, { maxAttempts: attempts });

  // Stops calling the executed function for 10 seconds if it fails 5 times in a row.
  const circuitBreakerPolicy = circuitBreaker(handleAll, {
    halfOpenAfter: 10 * 1000,
    breaker: new ConsecutiveBreaker(5),
  });

  const retryWithBreaker = wrap(retryPolicy, circuitBreakerPolicy);

  const result = await retryWithBreaker.execute(() => {
    return fetchData;
  });

  return result;
}

export async function handleExternalReqResCodes(
  res: ExternalClientIEnumerableExternalPartnerResponseResponse,
  onSuccess: (
    res: ExternalClientIEnumerableExternalPartnerResponseResponse,
  ) => void,
  onError: (
    res: ExternalClientIEnumerableExternalPartnerResponseResponse,
  ) => void,
  initiateAuth: () => Promise<null | undefined>,
) {
  switch (res.responseCode) {
    case 200:
    case 201:
    case 202:
      onSuccess(res);
      break;
    case 401:
      initiateAuth();
      break;
    default:
      onError(res); // Now passes the response to the error callback
  }
}

/**
 * Recursively finds and merges new folder data into existing folder structure
 * @param existingFolders - The current folder structure
 * @param newFolders - The new folders/documents to merge
 * @param targetFolderId - The ID of the folder to merge into (if undefined, returns newFolders)
 * @returns The merged folder array
 */
export function mergeFolderData(
  existingFolders: Array<ExternalFolder>,
  newFolders: Array<ExternalFolder>,
  targetFolderId: string | undefined,
): Array<ExternalFolder> {
  if (!targetFolderId) {
    // Initial load: return new folders
    return newFolders;
  }

  // Find the target folder in the existing structure and merge the new data
  const findAndMerge = (
    folders: Array<ExternalFolder>,
  ): Array<ExternalFolder> => {
    return folders.map((folder) => {
      if (folder.id === targetFolderId) {
        // Found the target folder, merge the new folders and documents
        // Get existing IDs to avoid duplicates
        const existingFolderIds = new Set(
          (folder.folders || []).map((f) => f.id).filter(Boolean),
        );
        const existingDocumentIds = new Set(
          (folder.documents || []).map((d) => d.id).filter(Boolean),
        );

        // Filter out duplicates from new folders
        const newFoldersToAdd =
          newFolders[0]?.folders
            ?.filter((f) => f.id && !existingFolderIds.has(f.id))
            .map((f) => ({ ...f, hasPreviouslySelected: false })) || [];

        // Filter out duplicates from new documents
        const newDocumentsToAdd =
          newFolders[0]?.documents?.filter(
            (d) => d.id && !existingDocumentIds.has(d.id),
          ) || [];

        return {
          ...folder,
          folders: [...(folder.folders || []), ...newFoldersToAdd],
          documents: [...(folder.documents || []), ...newDocumentsToAdd],
        };
      }
      // Recursively search in nested folders
      if (folder.folders && folder.folders.length > 0) {
        return {
          ...folder,
          folders: findAndMerge(folder.folders),
        };
      }
      return folder;
    });
  };

  return findAndMerge(existingFolders);
}

/**
 * Checks if a folder already has children (folders or documents) loaded
 * @param folders - The folder structure to search through
 * @param folderId - The ID of the folder to check
 * @returns True if the folder has children loaded, false otherwise
 */
export function hasFolderChildrenLoaded(
  folders: Array<ExternalFolder>,
  folderId: string | undefined,
): boolean {
  if (!folderId || !folders || folders.length === 0) {
    return false;
  }

  const findFolder = (folderList: Array<ExternalFolder>): boolean => {
    for (const folder of folderList) {
      if (folder.id === folderId) {
        // Check if this folder has any folders or documents loaded
        return (
          Boolean(folder.folders && folder.folders.length > 0) ||
          Boolean(folder.documents && folder.documents.length > 0)
        );
      }
      // Recursively search in nested folders
      if (folder.folders && folder.folders.length > 0) {
        if (findFolder(folder.folders)) {
          return true;
        }
      }
    }
    return false;
  };

  return findFolder(folders);
}

/**
 * Recursively updates a folder's flag to mark it as previously selected
 * @param folders - The folder structure to update
 * @param folderId - The ID of the folder to update
 * @returns The updated folder structure with the flag set
 */
export function updateFolderFlag<T extends ExternalFolder>(
  folders: Array<T>,
  folderId: string | undefined,
): Array<T> {
  if (!folderId) return folders;

  return folders.map((folder) => {
    if (folder.id === folderId) {
      return { ...folder, hasPreviouslySelected: true } as T;
    }
    if (folder.folders && folder.folders.length > 0) {
      return {
        ...folder,
        folders: updateFolderFlag(folder.folders, folderId),
      } as T;
    }
    return folder;
  });
}

/**
 * Recursively finds a folder by ID in a folder structure
 * @param folders - The folder structure to search
 * @param folderId - The ID of the folder to find
 * @returns The folder if found, undefined otherwise
 */
export function findFolderRecursive<T extends ExternalFolder>(
  folders: Array<T>,
  folderId: string | undefined,
): T | undefined {
  if (!folderId || !folders || folders.length === 0) {
    return undefined;
  }

  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }
    if (folder.folders && folder.folders.length > 0) {
      const found = findFolderRecursive(folder.folders, folderId);
      if (found) return found as T;
    }
  }
  return undefined;
}

/**
 * Extracts folder keys from a tree data structure
 * @param treeData - The tree data to extract keys from
 * @returns A Set of folder keys (strings)
 */
export function extractFolderKeysFromTree(
  treeData: Array<{
    key?: React.Key;
    selectable?: boolean;
    isLeaf?: boolean;
    children?: unknown[];
  }>,
): Set<string> {
  const keys = new Set<string>();
  const extractRecursive = (
    nodes: Array<{
      key?: React.Key;
      selectable?: boolean;
      isLeaf?: boolean;
      children?: unknown[];
    }>,
  ): void => {
    nodes.forEach((node) => {
      // Folders have selectable: true and are not leaves
      if (node.selectable && !node.isLeaf && node.key) {
        keys.add(node.key.toString());
        if (node.children && node.children.length > 0) {
          extractRecursive(
            node.children as Array<{
              key?: React.Key;
              selectable?: boolean;
              isLeaf?: boolean;
              children?: unknown[];
            }>,
          );
        }
      }
    });
  };
  extractRecursive(treeData);
  return keys;
}

/**
 * Finds the first folder key in a tree data structure
 * @param treeData - The tree data to search
 * @returns The first folder key found, or undefined if no folder exists
 */
export function findFirstFolderKey(
  treeData: Array<{
    key?: React.Key;
    selectable?: boolean;
    isLeaf?: boolean;
    children?: unknown[];
  }>,
): React.Key | undefined {
  const findRecursive = (
    nodes: Array<{
      key?: React.Key;
      selectable?: boolean;
      isLeaf?: boolean;
      children?: unknown[];
    }>,
  ): React.Key | undefined => {
    for (const node of nodes) {
      // Folders are nodes that are not leaves (isLeaf is false or undefined) and have a key
      // They may have selectable: true (in transformDataBpToExternal) or not (in transformDataExternalToBp)
      // But they always have isLeaf: false or undefined
      const isFolder =
        node.key &&
        (node.isLeaf === false ||
          (!node.isLeaf && node.children !== undefined));

      if (isFolder) {
        return node.key;
      }

      // Recursively search children if present
      if (node.children && node.children.length > 0) {
        const found = findRecursive(
          node.children as Array<{
            key?: React.Key;
            selectable?: boolean;
            isLeaf?: boolean;
            children?: unknown[];
          }>,
        );
        if (found) return found;
      }
    }
    return undefined;
  };
  return findRecursive(treeData);
}

/**
 * Handles folder selection logic including expansion and loading children
 * @param selectedKeys - The keys that were selected
 * @param folderKeys - Set of folder keys
 * @param mappedExternalFolders - The current folder structure
 * @param expandedKeys - Current expanded keys
 * @param setExpandedKeys - Function to update expanded keys
 * @param setSelectedFolderId - Function to set the selected folder ID
 * @returns The selected folder key if one was selected, undefined otherwise
 */
export function handleFolderSelection(
  selectedKeys: React.Key[],
  folderKeys: Set<string>,
  mappedExternalFolders: Array<ExtendedExternalFolder> | undefined,
  expandedKeys: React.Key[],
  setExpandedKeys: (keys: React.Key[]) => void,
  setSelectedFolderId: (id: string | undefined) => void,
): string | undefined {
  // Check if any selected key is a folder
  const hasFolderSelected = selectedKeys.some((key) =>
    folderKeys.has(key.toString()),
  );

  if (!hasFolderSelected) {
    return undefined;
  }

  const selectedFolderKey = selectedKeys
    .find((key) => folderKeys.has(key.toString()))
    ?.toString();

  if (!selectedFolderKey) {
    return undefined;
  }

  // Automatically expand the selected folder
  if (!expandedKeys.includes(selectedFolderKey)) {
    setExpandedKeys([...expandedKeys, selectedFolderKey]);
  }

  const hasBeenViewed = findFolderRecursive(
    mappedExternalFolders || [],
    selectedFolderKey,
  )?.hasPreviouslySelected;

  // Check if the folder already has children loaded to prevent unnecessary API calls
  const alreadyHasChildren = hasFolderChildrenLoaded(
    mappedExternalFolders || [],
    selectedFolderKey,
  );

  if (!alreadyHasChildren && !hasBeenViewed) {
    setSelectedFolderId(selectedFolderKey);
  }

  return selectedFolderKey;
}

/**
 * Adds a new folder to the folder structure immutably
 * @param folders - The current folder structure
 * @param newFolder - The new folder to add
 * @param parentFolderId - The ID of the parent folder to add the new folder to
 * @returns The updated folder structure with the new folder added
 */
export function addFolderToStructure<T extends ExtendedExternalFolder>(
  folders: Array<T>,
  newFolder: T,
  parentFolderId: string | undefined,
): Array<T> {
  if (!parentFolderId) {
    // If no parent, add to root level
    return [...folders, newFolder];
  }

  const updateFolders = (folderList: Array<T>): Array<T> => {
    return folderList.map((folder) => {
      if (folder.id === parentFolderId) {
        // Found the parent folder, add the new folder
        return {
          ...folder,
          folders: [...(folder.folders || []), newFolder],
        } as T;
      }
      // Recursively search in nested folders
      if (folder.folders && folder.folders.length > 0) {
        return {
          ...folder,
          folders: updateFolders(folder.folders as Array<T>),
        } as T;
      }
      return folder;
    });
  };

  return updateFolders(folders);
}
